import * as z from 'zod';
import { naicsCodeSchema, financialPeriodPreparationMethodSchema } from '../../enums.mjs';
import { partialAddressSchema } from '../../types/address/address.mjs';
import { suretyAccountId, isoDateTimeSchema, suretyQuoteId, isoDateSchema, individualContactId } from '../../types/opaque.mjs';
import { BalanceSheetData } from '../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import { IncomeStatementData } from '../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import '../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import '../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import '../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import { currencySchema } from '../../types/utils.mjs';

const bondingLineSchema = z.object({
    rate: z.number(),
    isPublished: z.boolean(),
    singleLimit: currencySchema,
    aggregateLimit: currencySchema,
});
const suretyMetadataSchema = z.object({
    contract: z
        .object({
        bondingLine: bondingLineSchema.nullable(),
    })
        .nullable(),
});
const accountSchema = z.object({
    id: suretyAccountId,
    displayName: z.string(),
    address: partialAddressSchema.nullable(),
    naicsCodes: z.array(naicsCodeSchema),
    suretyMetadata: suretyMetadataSchema,
});
const quoteRowSchema = z.object({
    id: suretyQuoteId,
    submittedAt: isoDateTimeSchema,
    state: z.string().nullable(),
    monthsToComplete: z.number().nullable(),
    description: z.string().nullable(),
    contractAmount: currencySchema.nullable(),
});
const portalActivitySchema = z.object({
    activeExposureUSD: currencySchema.nullable(),
    openBonds: z.number().int(),
    cumulativeFaceUSD: currencySchema.nullable(),
    firstBondDate: isoDateSchema.nullable(),
    lastBondDate: isoDateSchema.nullable(),
    trailing90dBonds: z.number().int(),
    quotes: z.array(quoteRowSchema),
});
const companyCreditSchema = z.object({
    companyName: z.string(),
    fsr: z.number().int().nullable(),
    bankruptcy: z.boolean(),
    judgmentBalance: currencySchema,
    lienBalance: currencySchema,
    pulledAt: isoDateTimeSchema,
});
const ownerCreditSchema = z.object({
    individualContactId: individualContactId,
    name: z.string(),
    fico: z.number().int(),
    ficoYoY: z.number().int().nullable(),
    bankruptcy: z.boolean(),
    pulledAt: isoDateTimeSchema,
});
const uwStateSchema = z.object({
    status: z.string(),
    score: z.number().nullable(),
    completenessScore: z.number().nullable(),
    reviewedAt: isoDateTimeSchema.nullable(),
    expiresAt: isoDateTimeSchema.nullable(),
    expired: z.boolean(),
});
const underwritingCreditSchema = z.object({
    premiumWritten: z.object({
        value: currencySchema.nullable(),
        count: z.number().int(),
    }),
    companyCredit: companyCreditSchema.nullable(),
    ownerCredit: z.array(ownerCreditSchema),
    uwState: uwStateSchema.nullable(),
    indemnitorOnly: z.array(z.object({ name: z.string() })),
});
const financialPeriodSchema = z.object({
    year: z.string(),
    periodEndDate: isoDateSchema,
    preparationMethod: financialPeriodPreparationMethodSchema,
    fileName: z.string().nullable(),
    balanceSheetOutput: BalanceSheetData.schema.nullable(),
    incomeStatementOutput: IncomeStatementData.schema.nullable(),
    backlog: currencySchema.nullable(),
});
const financialsSchema = z.object({
    periods: z.array(financialPeriodSchema),
});
var RenderReportPayload;
(function (RenderReportPayload) {
    RenderReportPayload.schema = z.object({
        account: accountSchema,
        tags: z.array(z.string()),
        portalActivity: portalActivitySchema,
        underwritingCredit: underwritingCreditSchema,
        financials: financialsSchema,
        generatedAt: isoDateTimeSchema,
        brief: z.array(z.object({ html: z.string() })).nullable(),
    });
})(RenderReportPayload || (RenderReportPayload = {}));

export { RenderReportPayload };
